import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  DtoV1AUSBorrowerCreditReference,
  DtoV1AUSDesktopUnderwriterCreditCompany,
  DtoV1AUSFreOffering,
  DtoV1AUSLoanProductAdvisorCreditCompany,
  DtoV1AUSRunAusWithReissueRequest,
} from '~/__generated__/api';
import Box from '~/common/components/Box';
import Oops from '~/common/components/Oops';
import { useSnackbar } from '~/common/components/Snackbar/snackbarStore';
import Spinner from '~/common/components/Spinner';
import { AUS_METHOD } from '~/common/constants';
import useGetLoanSummary from '~/common/hooks/useGetLoanSummary';
import useUrl from '~/common/hooks/useUrl';
import Button from '~/common/mui/Buttons/MUIButton/MUIButton';
import Divider from '~/common/mui/Divider';
import { typedApiClient } from '~/common/util/api-client';
import { getFieldErrorCount } from '~/common/util/formUtils';
import { hexToRgba } from '~/common/util/format';
import palette from '~/theme/mui/palette';

import AUSLoaderScreen from './AUSLoaderScreen';
import type { AUSErrorSection } from './AUSType';
import RunAUSLeftColumn from './RunAUSLeftColumn';
import RightColumn from './RunAUSRightColumn';
import {
  buildAUSPayload,
  getAgencyOptions,
  getAgencyValue,
  getInitialAgency,
} from './ausUtils';
import { useAppForm, useStore } from './hooks/ausFormHook';
import { useAUSDetailsQuery } from './hooks/useAUSDetailsQuery';
import useAUSStore from './useAUSStore';

type BorrowerCreditReference = DtoV1AUSBorrowerCreditReference;
type DUCreditCompany = DtoV1AUSDesktopUnderwriterCreditCompany;
type FreOffering = DtoV1AUSFreOffering;
type LPACreditCompany = DtoV1AUSLoanProductAdvisorCreditCompany;
type RunAusWithReissueRequest = DtoV1AUSRunAusWithReissueRequest;

const AUTO_OFFERING_OPTION = {
  value: 'AUTO',
  label: '(Detect Automatically)',
  key: 'AUTO',
} as const;

const RunAUSPage = () => {
  const {
    obfuscatedLoanIdentifier: {
      obfuscatedLenderDatabaseId,
      obfuscatedLoanRecordId,
    },
  } = useUrl();
  const showSnackbar = useSnackbar();

  const { data: ausDetails, isLoading, isError } = useAUSDetailsQuery();
  const { data: loanSummary } = useGetLoanSummary();

  const {
    isAUSLoaderVisible,
    isRunningAUS,
    isAUSRunSuccessful,
    setUseBrokerCredentials,
    useBrokerCredentials,
    ausDetailsLoading,
    ausRunStartedState,
    ausRunErrorState,
    ausRunSuccessState,
    creditOption,
    setCreditOption,
  } = useAUSStore();

  const {
    ausDetails: {
      desktopUnderwriterDetails = {},
      loanProductAdvisorDetails = {},
      borrowerCreditReferences = [],
      positiveRentalPaymentIndicator: rawPositiveRentalPaymentIndicator,
      positiveRentalPaymentText,
    } = {},
    lastAusRunDetails = {},
    lastAusMethodDetails = {},
  } = ausDetails ?? {};

  const {
    creditCompanies = [],
    loanAssociationDetails = {},
    brokerAssociatedCreditCompanies = [],
    hasDesktopUnderwriterData = false,
  } = desktopUnderwriterDetails;

  const defaultUseBrokerCredentials =
    loanAssociationDetails.brokerCredentialsInd ?? false;
  const userName = loanAssociationDetails.username ?? '';
  const password = loanAssociationDetails.password ?? '';

  const {
    creditCompanies: lpaCreditCompanies = [],
    loanAssociationDetails: lpaLoanAssociationDetails = {},
    freOfferingDetails = [],
    hasLoanProductAdvisorData = false,
  } = loanProductAdvisorDetails;

  const lpaBranchNumber = lpaLoanAssociationDetails.branchNumber ?? '';
  const freOfferingId = lpaLoanAssociationDetails.freOfferingId ?? 'AUTO';
  const positiveRentalPaymentIndicator =
    rawPositiveRentalPaymentIndicator ?? false;

  const hasOneClickAUS = hasDesktopUnderwriterData && hasLoanProductAdvisorData;
  const formDisabled = creditOption === 'existing';
  const isUseBrokerCredentialsDisabled =
    brokerAssociatedCreditCompanies.length === 0;

  const [errorScrollTrigger, setErrorScrollTrigger] = useState(0);
  const [firstErrorSection, setFirstErrorSection] =
    useState<AUSErrorSection | null>(null);
  const rawLastRunAUS = ausDetails?.lastAusRunDetails?.requestMethod;
  const lastRunAUS: (typeof AUS_METHOD)[keyof typeof AUS_METHOD] =
    (typeof rawLastRunAUS === 'number'
      ? AUS_METHOD[rawLastRunAUS]
      : rawLastRunAUS) ?? 'DU';
  const [selectedAusOption, setSelectedAusOption] = useState(lastRunAUS);

  const activeAusMethodDetails =
    selectedAusOption === 'DU'
      ? lastAusMethodDetails?.desktopUnderwriterDetails
      : lastAusMethodDetails?.loanProductAdvisorDetails;

  const activeAusDetails =
    hasOneClickAUS && creditOption === 'existing'
      ? activeAusMethodDetails
      : lastAusRunDetails;

  const {
    requestStatus: lastRunAUSStatus = '',
    recommendation: lastRunAUSRecommendation = '',
  } = activeAusDetails ?? {};

  const prevCreditOptionRef = useRef(creditOption);
  const prevUseBrokerCredentialsRef = useRef<boolean>(useBrokerCredentials);

  const form = useAppForm({
    defaultValues: {
      userName: '',
      password: '',
      agency: '',
      freOfferingId: 'AUTO',
      lpaBranchNumber: '',
      positiveRentalPaymentHistory: false,
    },
  });

  const watchedAgency = useStore(form.store, (s) => s.values.agency);
  const watchedUserName = useStore(form.store, (s) => s.values.userName);
  const watchedPassword = useStore(form.store, (s) => s.values.password);

  // Memoized values
  const agencyOptions = useMemo(
    () =>
      getAgencyOptions(
        selectedAusOption,
        useBrokerCredentials,
        lpaCreditCompanies,
        brokerAssociatedCreditCompanies,
        creditCompanies,
      ),
    [
      selectedAusOption,
      useBrokerCredentials,
      lpaCreditCompanies,
      brokerAssociatedCreditCompanies,
      creditCompanies,
    ],
  );

  const freOfferingOptions = useMemo(() => {
    if (freOfferingDetails.length === 0) {
      return [AUTO_OFFERING_OPTION];
    }

    const detectAutoRaw = freOfferingDetails.find(
      (item: FreOffering) => item.freOfferingId === 'AUTO',
    );

    const detectAuto = detectAutoRaw
      ? {
          value: detectAutoRaw.freOfferingId,
          label: detectAutoRaw.displayText,
          key: detectAutoRaw.freOfferingId,
        }
      : AUTO_OFFERING_OPTION;

    const otherOptions = freOfferingDetails
      .filter((item: FreOffering) => item.freOfferingId !== 'AUTO')
      .map((item: FreOffering) => ({
        value: item.freOfferingId,
        label: item.displayText,
        key: item.freOfferingId,
      }));

    return [detectAuto, ...otherOptions];
  }, [freOfferingDetails]);

  // Helper to reset all form fields
  const resetFormFields = () => {
    form.setFieldValue('userName', userName);
    form.setFieldValue('password', password);
    form.setFieldValue(
      'agency',
      String(
        getAgencyValue(
          selectedAusOption,
          loanAssociationDetails,
          lpaLoanAssociationDetails,
        ),
      ),
    );
    form.setFieldValue('freOfferingId', freOfferingId);
    form.setFieldValue('lpaBranchNumber', lpaBranchNumber);
    borrowerCreditReferences.forEach((borrower: BorrowerCreditReference) => {
      const { creditReference = '', borrowerIdentifier } = borrower;
      const fieldName = `ref_${borrowerIdentifier?.customerRecordId}`;
      // Dynamic field name (ref_<id>) is not part of the statically-typed form defaultValues,
      // so the cast is required here.
      (form.setFieldValue as (name: string, value: string) => void)(
        fieldName,
        creditReference,
      );
    });
    setUseBrokerCredentials(defaultUseBrokerCredentials);
  };

  // Memoized selected broker company
  const selectedBrokerCompany = useMemo(() => {
    if (!watchedAgency || watchedAgency === 'Select one') return undefined;

    // DU with broker credentials
    if (selectedAusOption === 'DU' && useBrokerCredentials) {
      return brokerAssociatedCreditCompanies.find(
        (company: DUCreditCompany) => company.creditCompany === watchedAgency,
      );
    }

    // DU without broker credentials
    if (selectedAusOption === 'DU' && !useBrokerCredentials) {
      return creditCompanies.find(
        (company: DUCreditCompany) => company.creditCompany === watchedAgency,
      );
    }

    // LPA
    if (selectedAusOption === 'LPA') {
      return lpaCreditCompanies.find(
        (company: LPACreditCompany) =>
          String(company.creditCompanyRecordId) === String(watchedAgency),
      );
    }
  }, [
    selectedAusOption,
    useBrokerCredentials,
    watchedAgency,
    brokerAssociatedCreditCompanies,
    creditCompanies,
    lpaCreditCompanies,
  ]);

  const ausRunMutation = useMutation({
    mutationFn: (payload: RunAusWithReissueRequest) =>
      typedApiClient.v1.ausRunAusWithReissue(payload),
    onSuccess: async () => {
      ausRunSuccessState();
    },
    onError: () => {
      ausRunErrorState();
      showSnackbar({
        severity: 'error',
        message: `${selectedAusOption} failed to run`,
        duration: 6000,
      });
    },
  });

  const savePositiveRentalPaymentMutation = useMutation({
    mutationFn: async (positiveRentalPaymentHistory: boolean) => {
      await typedApiClient.v1.ausPositiveRentalPayment({
        obfuscatedLoanIdentifier: {
          obfuscatedLenderDatabaseId,
          obfuscatedLoanRecordId,
        },
        positiveRentalHistoryValue: positiveRentalPaymentHistory,
      });
    },
    onError: () => {
      showSnackbar({
        severity: 'error',
        message: `${selectedAusOption} failed to save positive rental payment history`,
        duration: 6000,
      });
    },
  });

  const updatedPositiveRentalPaymentIndicator = useCallback(
    (positiveRentalPaymentChecked: boolean) => {
      form.setFieldValue(
        'positiveRentalPaymentHistory',
        positiveRentalPaymentChecked,
      );
    },
    [form],
  );

  const handleRunAUS = async () => {
    const validationErrors = await form.validateAllFields('submit');
    if (validationErrors.some(Boolean)) {
      const fieldMeta = form.store.state.fieldMeta;
      let section: AUSErrorSection | null = null;

      if (getFieldErrorCount(fieldMeta, 'agency') > 0) {
        section = 'agency';
      } else if (
        selectedAusOption === 'DU' &&
        (getFieldErrorCount(fieldMeta, 'userName') > 0 ||
          getFieldErrorCount(fieldMeta, 'password') > 0)
      ) {
        section = 'credentials';
      } else if (
        selectedAusOption === 'LPA' &&
        (getFieldErrorCount(fieldMeta, 'freOfferingId') > 0 ||
          getFieldErrorCount(fieldMeta, 'lpaBranchNumber') > 0)
      ) {
        section = 'lpa';
      } else {
        const hasCreditReferenceError = borrowerCreditReferences.some(
          (borrower: BorrowerCreditReference) => {
            const fieldName = `ref_${borrower.borrowerIdentifier?.customerRecordId}`;
            return getFieldErrorCount(fieldMeta, fieldName) > 0;
          },
        );

        if (hasCreditReferenceError) {
          section = 'creditRefs';
        }
      }

      setFirstErrorSection(section);
      setErrorScrollTrigger((prev) => prev + 1);
      return;
    }

    setFirstErrorSection(null);

    const formData = form.state.values;
    ausRunStartedState(lastRunAUSRecommendation);
    const payload = buildAUSPayload({
      formData,
      selectedAusOption,
      creditOption,
      lastRunAUSStatus,
      lastRunAUSRecommendation,
      lpaLoanAssociationDetails,
      loanAssociationDetails,
      creditCompanies,
      borrowerCreditReferences,
      useBrokerCredentials,
      obfuscatedLenderDatabaseId,
      obfuscatedLoanRecordId,
    });

    ausRunMutation.mutate(payload);
    savePositiveRentalPaymentMutation.mutate(
      formData.positiveRentalPaymentHistory,
    );
  };

  const resetFormFieldsWhilePreservingFreOfferingId = () => {
    const currentFreOfferingId = form.state.values.freOfferingId;
    resetFormFields();
    if (currentFreOfferingId) {
      form.setFieldValue('freOfferingId', currentFreOfferingId);
    }
  };

  useEffect(() => {
    // Reset fields and clear errors when switching TO "existing"
    if (creditOption === 'existing') {
      if (prevCreditOptionRef.current !== 'existing') {
        setSelectedAusOption(lastRunAUS);
        resetFormFieldsWhilePreservingFreOfferingId();
      }
      prevCreditOptionRef.current = creditOption;
      prevUseBrokerCredentialsRef.current = useBrokerCredentials;
      return;
    }

    // Clear username/password if agency is not selected
    if (
      selectedAusOption === 'DU' &&
      useBrokerCredentials &&
      (!watchedAgency || watchedAgency === '' || watchedAgency === 'Select one')
    ) {
      form.setFieldValue('userName', '');
      form.setFieldValue('password', '');
      prevCreditOptionRef.current = creditOption;
      prevUseBrokerCredentialsRef.current = useBrokerCredentials;
      return;
    }

    // Set broker credentials if agency and company are selected
    if (
      selectedAusOption === 'DU' &&
      useBrokerCredentials &&
      selectedBrokerCompany &&
      'username' in selectedBrokerCompany &&
      'creditCompany' in selectedBrokerCompany
    ) {
      form.setFieldValue('agency', selectedBrokerCompany.creditCompany);
      form.setFieldValue('userName', selectedBrokerCompany.username ?? '');
      form.setFieldValue('password', selectedBrokerCompany.password ?? '');
      prevCreditOptionRef.current = creditOption;
      prevUseBrokerCredentialsRef.current = useBrokerCredentials;
      return;
    }

    // Handle reissue option
    if (
      selectedAusOption === 'DU' &&
      creditOption === 'reissue' &&
      prevCreditOptionRef.current !== 'reissue'
    ) {
      form.setFieldValue('userName', '');
      form.setFieldValue('password', '');
      setUseBrokerCredentials(false);
      prevCreditOptionRef.current = creditOption;
      prevUseBrokerCredentialsRef.current = useBrokerCredentials;
      return;
    }

    prevCreditOptionRef.current = creditOption;
    prevUseBrokerCredentialsRef.current = useBrokerCredentials;
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [
    selectedAusOption,
    useBrokerCredentials,
    creditOption,
    lastRunAUS,
    selectedBrokerCompany,
    watchedAgency,
    watchedUserName,
    watchedPassword,
  ]);

  useEffect(() => {
    // When switching credential mode, reset agency to 'Select one'
    if (
      creditOption !== 'existing' &&
      selectedAusOption === 'DU' &&
      !useBrokerCredentials &&
      watchedAgency
    ) {
      form.setFieldValue('userName', '');
      form.setFieldValue('password', '');
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [useBrokerCredentials, selectedAusOption]);

  useEffect(() => {
    setSelectedAusOption(lastRunAUS);
  }, [lastRunAUS]);

  useEffect(() => {
    if (creditOption === 'reissue' && selectedAusOption === 'DU') {
      setUseBrokerCredentials(defaultUseBrokerCredentials);
      if (userName) {
        form.setFieldValue('userName', userName);
      }
      if (password) {
        form.setFieldValue('password', password);
      }
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [creditOption]);

  useEffect(() => {
    if (ausDetails) {
      const resolvedAgency = getInitialAgency(
        lastRunAUS,
        lpaLoanAssociationDetails,
        loanAssociationDetails,
      );
      form.setFieldValue('userName', userName);
      form.setFieldValue('password', password);
      form.setFieldValue('agency', resolvedAgency || 'Select one');
      form.setFieldValue(
        'freOfferingId',
        freOfferingDetails.length === 0 ? 'AUTO' : freOfferingId,
      );
      form.setFieldValue('lpaBranchNumber', lpaBranchNumber);
      form.setFieldValue(
        'positiveRentalPaymentHistory',
        positiveRentalPaymentIndicator,
      );
      borrowerCreditReferences.forEach((borrower: BorrowerCreditReference) => {
        const { creditReference = '', borrowerIdentifier } = borrower;
        const fieldName = `ref_${borrowerIdentifier?.customerRecordId}`;
        // Dynamic field name (ref_<id>) is not part of the statically-typed form defaultValues,
        // so the cast is required here.
        (form.setFieldValue as (name: string, value: string) => void)(
          fieldName,
          creditReference,
        );
      });
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [ausDetails]);

  if (isLoading) {
    return (
      <Spinner
        height="100%"
        backgroundColor={palette.white}
        loadingMessage="One moment, loading..."
      />
    );
  }

  if (isError) {
    return <Oops />;
  }

  return (
    <form.AppForm>
      <Box
        sx={{
          display: 'flex',
          height: '100%',
          flexDirection: 'column',
          justifyContent: 'space-between',
          position: 'relative',
        }}
      >
        {isAUSLoaderVisible && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 10000,
            }}
          >
            {ausDetailsLoading || !ausDetails ? (
              <Spinner
                height="100%"
                backgroundColor={hexToRgba(palette.white, 0.5)}
              />
            ) : (
              <AUSLoaderScreen
                ausDetailsResponse={ausDetails}
                dti={String(loanSummary?.loanInfo?.dti ?? '')}
                hti={String(loanSummary?.loanInfo?.hti ?? '')}
                ltv={String(loanSummary?.loanInfo?.ltv ?? '')}
                creditScore={String(loanSummary?.loanInfo?.fico ?? '')}
              />
            )}
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            height: '100%',
            overflowY: 'auto',
            width: '100%',
            position: 'relative',
            justifyContent: 'space-around',
          }}
        >
          <RunAUSLeftColumn selectedOption={selectedAusOption} />
          <Divider
            orientation="vertical"
            sx={{
              margin: '0 1.5rem',
              border: '1px solid',
              height: 'auto',
              borderColor: palette.gray[300],
            }}
          />
          <RightColumn
            form={form}
            creditOption={creditOption}
            setCreditOption={setCreditOption}
            selectedAusOption={selectedAusOption}
            setSelectedAusOption={setSelectedAusOption}
            formDisabled={formDisabled}
            lastRunAUS={lastRunAUS}
            agencyOptions={agencyOptions}
            getInitialAgency={getInitialAgency}
            setUseBrokerCredentials={setUseBrokerCredentials}
            useBrokerCredentials={useBrokerCredentials}
            isUseBrokerCredentialsDisabled={isUseBrokerCredentialsDisabled}
            freOfferingOptions={freOfferingOptions}
            borrowerCreditReferences={borrowerCreditReferences}
            lpaLoanAssociationDetails={lpaLoanAssociationDetails}
            loanAssociationDetails={loanAssociationDetails}
            positiveRentalPaymentIndicator={positiveRentalPaymentIndicator}
            positiveRentalPaymentText={positiveRentalPaymentText}
            updatedPositiveRentalPaymentIndicator={
              updatedPositiveRentalPaymentIndicator
            }
            hasOneClickAUS={hasOneClickAUS}
            loanProductType={String(
              loanSummary?.loanInfo?.loanProductType ?? '',
            )}
            firstErrorSection={firstErrorSection}
            errorScrollTrigger={errorScrollTrigger}
          />
        </Box>
        {isAUSRunSuccessful === null && (
          <Box
            component={'footer'}
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleRunAUS}
              isDisabled={isRunningAUS}
            >
              {isRunningAUS
                ? `Running ${selectedAusOption} ...`
                : `Run ${selectedAusOption}`}
            </Button>
          </Box>
        )}
      </Box>
    </form.AppForm>
  );
};

export default RunAUSPage;
